import React from "react";
import { CreateVercelProjectModal } from "@/components/ui/CreateVercelProjectModal";
import { Plus, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Combobox } from "@/components/ui/Combobox";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { Alert } from "@/components/ui/alert";

type Site = {
  id: number;
  name: string;
  subdomain: string;
  custom_domain: string;
  description: string;
  keywords: string;
  deployer_user_id?: string | number;
  vercel_project_id?: string | number;
  activated: boolean;
  [key: string]: any;
};

type SiteEditFormProps = {
  values: {
    name: string;
    subdomain: string;
    custom_domain: string;
    description: string;
    keywords: string;
    deployer_user_id: string;
    vercel_project_id: string;
    activated: boolean;
  };
  onChange: (field: keyof SiteEditFormProps["values"], value: any) => void;
  onSubmit: (e: React.FormEvent) => void;
  isPending: boolean;
  isError: boolean;
  errorMessage?: string;
  collaborators: Array<{ id: string | number; email?: string; name?: string }>;
  loadingCollaborators: boolean;
  vercelProjects: Array<{ id: string | number; name?: string }>;
  loadingVercelProjects: boolean;
  vercelModalOpen: boolean;
  setVercelModalOpen: (open: boolean) => void;
  onVercelProjectCreated: () => void;
  logoUrl?: string;
  onLogoChange?: (file: File | null) => void;
  uploadingLogo?: boolean;
};

const SiteEditForm: React.FC<SiteEditFormProps> = ({
  values,
  onChange,
  onSubmit,
  isPending,
  isError,
  errorMessage,
  collaborators,
  loadingCollaborators,
  vercelProjects,
  loadingVercelProjects,
  vercelModalOpen,
  setVercelModalOpen,
  onVercelProjectCreated,
  logoUrl,
  onLogoChange,
  uploadingLogo,
}) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [isObjectUrl, setIsObjectUrl] = React.useState(false);

  React.useEffect(() => {
    if (logoUrl) {
      setPreviewUrl(logoUrl);
      setIsObjectUrl(false);
    }
  }, [logoUrl]);

  // Cleanup object URLs when component unmounts or previewUrl changes
  React.useEffect(() => {
    return () => {
      if (previewUrl && isObjectUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl, isObjectUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Revoke previous object URL if it exists
      if (previewUrl && isObjectUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      
      // Create a preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
      setIsObjectUrl(true);
      
      // Call the parent handler
      if (onLogoChange) {
        onLogoChange(file);
      }
    }
  };

  const handleRemoveLogo = () => {
    // Revoke object URL if it exists
    if (previewUrl && isObjectUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setPreviewUrl(null);
    setIsObjectUrl(false);
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (onLogoChange) {
      onLogoChange(null);
    }
  };

  if (!values) {
    return (
      <Alert variant="destructive" className="mb-2">
        Form values are not loaded.
      </Alert>
    );
  }
  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Name</Label>
        <Input
          id="name"
          value={values.name}
          onChange={e => onChange("name", e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="subdomain">Subdomain</Label>
        <Input
          id="subdomain"
          value={values.subdomain}
          onChange={e => onChange("subdomain", e.target.value)}
          required
        />
      </div>
      <div>
        <Label htmlFor="custom_domain">Custom domain</Label>
        <Input
          id="custom_domain"
          value={values.custom_domain}
          onChange={e => onChange("custom_domain", e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={values.description}
          onChange={e => onChange("description", e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="keywords">Keywords</Label>
        <Input
          id="keywords"
          value={values.keywords}
          onChange={e => onChange("keywords", e.target.value)}
        />
      </div>
      <div>
        <Label htmlFor="logo">Logo</Label>
        <div className="space-y-2">
          {previewUrl && (
            <div className="relative inline-block">
              <img 
                src={previewUrl} 
                alt="Logo preview" 
                className="h-24 w-auto border rounded"
              />
              <Button
                type="button"
                size="icon"
                variant="destructive"
                className="absolute -top-2 -right-2 h-6 w-6"
                onClick={handleRemoveLogo}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input
              id="logo"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              ref={fileInputRef}
              className="hidden"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingLogo}
            >
              <Upload className="h-4 w-4 mr-2" />
              {uploadingLogo ? "Uploading..." : previewUrl ? "Change Logo" : "Upload Logo"}
            </Button>
          </div>
        </div>
      </div>
      <div>
        <Label htmlFor="deployer_user_id">Deployer User</Label>
        <Select
          value={String(values.deployer_user_id)}
          onValueChange={val => {
            if (val && val !== "loading") {
              onChange("deployer_user_id", val);
            }
          }}
          required
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select deployer user" />
          </SelectTrigger>
          <SelectContent>
            {loadingCollaborators ? (
              <SelectItem value="loading" disabled>
                Loading...
              </SelectItem>
            ) : (
              <>
                {collaborators &&
                  collaborators.map(user => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.email || user.name || user.id}
                    </SelectItem>
                  ))}
                {/* Add fallback option if current value is not in collaborators */}
                {values.deployer_user_id &&
                  !collaborators.some(
                    user => String(user.id) === String(values.deployer_user_id)
                  ) && (
                    <SelectItem
                      key={String(values.deployer_user_id)}
                      value={String(values.deployer_user_id)}
                    >
                      Unknown User (ID: {values.deployer_user_id})
                    </SelectItem>
                  )}
              </>
            )}
          </SelectContent>
        </Select>
      </div>
      <div>
        <div className="flex items-center justify-between">
          <Label htmlFor="vercel_project_id">Vercel Project</Label>
          <Button
            type="button"
            size="icon"
            variant="outline"
            className="ml-2"
            onClick={() => setVercelModalOpen(true)}
            aria-label="Create new Vercel project"
          >
            <Plus className="h-4 w-4" />
            <span className="sr-only">Create new Vercel project</span>
          </Button>
        </div>
        <Combobox
          value={values.vercel_project_id}
          onChange={val => onChange("vercel_project_id", val)}
          options={
            loadingVercelProjects
              ? []
              : (vercelProjects || []).map(project => ({
                  value: project.id,
                  label: project.name || String(project.id),
                }))
          }
          placeholder="Select Vercel project"
          className="w-full"
          disabled={loadingVercelProjects}
        />
        <CreateVercelProjectModal
          open={vercelModalOpen}
          onOpenChange={setVercelModalOpen}
          onSuccess={onVercelProjectCreated}
        />
      </div>
      <div className="flex items-center space-x-2">
        <input
          id="activated"
          type="checkbox"
          checked={values.activated}
          onChange={e => onChange("activated", e.target.checked)}
          className="accent-primary h-4 w-4"
        />
        <Label htmlFor="activated" className="mb-0">Activated</Label>
      </div>
      {isError && (
        <Alert variant="destructive" className="mb-2">
          {errorMessage}
        </Alert>
      )}
      <Button type="submit" disabled={isPending}>
        {isPending ? "Updating..." : "Update Site"}
      </Button>
    </form>
  );
};

export default SiteEditForm;
